import { diffArrays, diffLines } from "diff";

const maxEditLength = 4000;
const comparisonTimeout = 1500;
const blockTags = new Set([
  "ADDRESS", "ARTICLE", "ASIDE", "BLOCKQUOTE", "CAPTION", "COL", "COLGROUP", "DD", "DETAILS", "DIV", "DL", "DT",
  "FIGCAPTION", "FIGURE", "FOOTER", "H1", "H2", "H3", "H4", "H5", "H6", "HEADER", "HR", "LI", "MAIN", "OL",
  "P", "PRE", "SECTION", "SUMMARY", "TABLE", "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "UL",
]);
const structuralTags = new Set(["DL", "OL", "UL", "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "COLGROUP"]);
const semanticAttributes = new Set([
  "href", "xlink:href", "src", "srcset", "alt", "title", "lang", "dir", "role", "aria-label", "aria-hidden",
  "datetime", "colspan", "rowspan", "scope", "start", "reversed", "value", "type", "download", "rel", "target",
  "poster", "controls", "loop", "muted", "autoplay", "preload", "media", "sizes", "kind", "srclang", "label",
  "viewBox", "d", "points", "cx", "cy", "r", "x", "y", "width", "height", "fill", "stroke", "transform",
]);
const idReferences = [
  "aria-labelledby", "aria-describedby", "aria-controls", "aria-owns", "aria-activedescendant", "aria-details",
  "aria-errormessage", "headers", "for", "list",
];
let comparisonSequence = 0;

// jsdiff's default Myers line tokens retain whitespace, CRLF, and the final
// newline. In particular, never normalize authored text or discard a negation.
export function compareContentText(before, after) {
  const changes = diffLines(before, after, { maxEditLength, timeout: comparisonTimeout });
  if (!changes) throw new Error("Content comparison exceeds the supported edit size");
  return changes;
}

function attributes(node) {
  return JSON.stringify(Array.from(node.attributes ?? [])
    .filter(({ name }) => semanticAttributes.has(name))
    .map(({ name, value }) => [name, value]).sort(([a], [b]) => a.localeCompare(b)));
}

function hasBlocks(node) {
  return Array.from(node.children ?? []).some((child) => blockTags.has(child.tagName));
}

function alignmentKey(node, keyAttribute) {
  if (keyAttribute && node.hasAttribute(keyAttribute)) return `key:${node.getAttribute(keyAttribute)}`;
  if (node.id) return `id:${node.id}`;
  // Definition terms provide stable alignment for unnumbered facts.
  const term = node.querySelector(":scope > dt, :scope > th");
  return term ? `term:${term.textContent}` : null;
}

// Inline runs remain a unit even in mixed containers such as an li containing
// text, a link, and a nested list. Aligning their individual children would turn
// an inline markup change into a misleading word-sized edit.
function children(document, node) {
  const result = [];
  let run = [];
  const flush = () => {
    if (run.some((child) => child.nodeType === 1 || child.textContent.trim())) {
      const span = document.createElement("span");
      for (const child of run) span.append(child.cloneNode(true));
      result.push(span);
    }
    run = [];
  };
  for (const child of node.childNodes) {
    if (child.nodeType === 1 && blockTags.has(child.tagName)) {
      flush();
      result.push(child);
    } else if (child.nodeType === 1 || child.nodeType === 3) {
      run.push(child);
    }
  }
  flush();
  return result;
}

// Ranges split only at source newlines or explicit br elements. Cloning the
// range carries links/emphasis across a line boundary without reparsing HTML.
function richLines(document, node) {
  const lines = [];
  const range = document.createRange();
  range.setStart(node, 0);
  let text = "";
  const finish = (end, offset) => {
    range.setEnd(end, offset);
    lines.push({ value: text, fragment: range.cloneContents() });
    range.setStart(end, offset);
    text = "";
  };
  const visit = (parent) => {
    for (let index = 0; index < parent.childNodes.length; index += 1) {
      const child = parent.childNodes[index];
      if (child.nodeType === 3) {
        let start = 0;
        for (const match of child.data.matchAll(/\r\n|\n/g)) {
          const end = match.index + match[0].length;
          text += child.data.slice(start, end);
          finish(child, end);
          start = end;
        }
        text += child.data.slice(start);
      } else if (child.nodeType === 1 && child.tagName === "BR") {
        text += "\n";
        finish(parent, index + 1);
      } else if (child.nodeType === 1) {
        visit(child);
      }
    }
  };
  visit(node);
  range.setEnd(node, node.childNodes.length);
  const remaining = range.cloneContents();
  if (text || !lines.length) lines.push({ value: text, fragment: remaining });
  else lines[lines.length - 1].fragment.append(remaining);
  return lines;
}

function isolateIds(document, root, origins) {
  let prefix;
  do {
    prefix = `content-comparison-${++comparisonSequence}-`;
  } while (document.querySelector(`[id^="${prefix}"]`));
  const maps = { before: new Map(), after: new Map() };
  let index = 0;
  const elements = Array.from(root.querySelectorAll("*"));
  for (const element of elements) {
    if (!element.id) continue;
    const map = maps[origins.get(element) ?? "after"];
    const original = element.id;
    element.id = `${prefix}${++index}`;
    if (!map.has(original)) map.set(original, element.id);
  }
  for (const element of elements) {
    const side = origins.get(element) ?? "after";
    const resolve = (id) => maps[side].get(id) ?? maps[side === "before" ? "after" : "before"].get(id) ?? id;
    for (const name of idReferences) {
      if (element.hasAttribute(name)) {
        element.setAttribute(name, element.getAttribute(name).split(/\s+/).map(resolve).join(" "));
      }
    }
    for (const name of ["href", "xlink:href"]) {
      const value = element.getAttribute(name);
      if (!value?.startsWith("#")) continue;
      let id = value.slice(1);
      try { id = decodeURIComponent(id); } catch { /* Preserve malformed authored fragments. */ }
      const resolved = resolve(id);
      if (resolved !== id) element.setAttribute(name, `#${resolved}`);
    }
    for (const attribute of Array.from(element.attributes)) {
      if (!attribute.value.includes("url(#")) continue;
      element.setAttribute(attribute.name, attribute.value.replace(/url\(#([^)]*)\)/g, (_, id) => `url(#${resolve(id)})`));
    }
  }
}

export function renderContentComparison(document, beforeNode, afterNode, locale, { metadataSelector = null, keyAttribute = null, disclaimer = "" } = {}) {
  const de = locale === "de-DE";
  const deadline = Date.now() + comparisonTimeout;
  const fingerprints = new WeakMap();
  const origins = new WeakMap();
  let inspected = 0;
  let changed = false;
  const checkWork = () => {
    if (Date.now() >= deadline) throw new Error("Content comparison exceeds the supported edit size");
  };
  const fingerprint = (node, depth = 0) => {
    if (fingerprints.has(node)) return fingerprints.get(node);
    if (++inspected > 50000 || depth > 128) throw new Error("Content comparison exceeds the supported document size");
    checkWork();
    let value = "";
    if (node.nodeType === 3) value = JSON.stringify(node.data);
    else if (node.nodeType === 1 || node.nodeType === 11) {
      const block = hasBlocks(node);
      const content = Array.from(node.childNodes)
        .filter((child) => !(block && child.nodeType === 3 && !child.data.trim()))
        .map((child) => fingerprint(child, depth + 1));
      value = `<${node.namespaceURI}:${node.tagName}${attributes(node)}>${content.join("")}</${node.tagName}>`;
    }
    fingerprints.set(node, value);
    return value;
  };
  const remember = (node, side) => {
    if (node.nodeType === 1) origins.set(node, side);
    for (const element of node.querySelectorAll?.("*") ?? []) origins.set(element, side);
    return node;
  };
  const clone = (node, side, deep = true) => remember(document.importNode(node, deep), side);
  const arrayChanges = (before, after, comparator) => {
    checkWork();
    const changes = diffArrays(before, after, { comparator, maxEditLength, timeout: Math.max(1, deadline - Date.now()) });
    if (!changes) throw new Error("Content comparison exceeds the supported edit size");
    return changes;
  };
  const mark = (node, kind) => {
    changed = true;
    node.setAttribute("data-content-change", kind);
    node.classList.add("relative", "border-s-2", "ps-7", "text-base-content");
    if (kind === "added") node.classList.add("border-success/60", "bg-success/10");
    else node.classList.add("border-error/60", "bg-error/10");
    const gutter = document.createElement("span");
    gutter.className = "absolute start-1 top-0 font-mono font-bold text-base-content";
    gutter.setAttribute("aria-hidden", "true");
    gutter.setAttribute("data-content-diff-label", "");
    gutter.textContent = kind === "added" ? "+" : "−";
    const label = document.createElement("span");
    label.className = "sr-only";
    label.setAttribute("data-content-diff-label", "");
    label.textContent = kind === "added" ? (de ? "Hinzugefügt: " : "Added: ") : (de ? "Entfernt: " : "Removed: ");
    node.prepend(gutter, label);
    return node;
  };
  const whole = (node, kind) => {
    const side = kind === "removed" ? "before" : "after";
    let result = clone(node, side, false);
    // Historical headings must not compete with the selected outline.
    if (kind === "removed" && /^H[1-6]$/.test(node.tagName)) {
      result = document.createElement("p");
      for (const attribute of node.attributes) result.setAttribute(attribute.name, attribute.value);
      result.classList.add("font-semibold", "text-lg");
      origins.set(result, side);
    }
    result.setAttribute("data-content-change", kind);
    changed = true;
    if (hasBlocks(node) || structuralTags.has(node.tagName)) {
      for (const child of children(document, node)) result.append(whole(child, kind));
      return result;
    }
    result.append(...Array.from(node.childNodes, (child) => clone(child, side)));
    if (node.tagName === "HR" || node.tagName === "COL") {
      result.setAttribute("aria-label", kind === "added" ? (de ? "Hinzugefügt" : "Added") : (de ? "Entfernt" : "Removed"));
      return result;
    }
    return mark(result, kind);
  };
  const lineBlock = (lines, kind) => {
    const span = document.createElement("span");
    span.className = "my-1 block min-w-0 whitespace-pre-wrap break-words py-1";
    for (const line of lines) span.append(clone(line.fragment, kind === "removed" ? "before" : "after"));
    return mark(span, kind);
  };
  const leaf = (before, after) => {
    const result = clone(after, "after", false);
    const oldLines = richLines(document, before);
    const newLines = richLines(document, after);
    const oldText = oldLines.map(({ value }) => value).join("");
    const newText = newLines.map(({ value }) => value).join("");
    // diffLines has no token for empty text, but an image-only line still
    // carries meaningful markup and must not disappear from the comparison.
    if (!oldText || !newText) {
      result.append(lineBlock(oldLines, "removed"), lineBlock(newLines, "added"));
      return result;
    }
    checkWork();
    const changes = compareContentText(oldText, newText);
    let oldIndex = 0;
    let newIndex = 0;
    for (const change of changes) {
      if (change.removed) {
        result.append(lineBlock(oldLines.slice(oldIndex, oldIndex + change.count), "removed"));
        oldIndex += change.count;
      } else if (change.added) {
        result.append(lineBlock(newLines.slice(newIndex, newIndex + change.count), "added"));
        newIndex += change.count;
      } else {
        const end = newIndex + change.count;
        while (newIndex < end) {
          if (fingerprint(oldLines[oldIndex].fragment) === fingerprint(newLines[newIndex].fragment)) {
            result.append(clone(newLines[newIndex++].fragment, "after"));
            oldIndex += 1;
            continue;
          }
          const oldStart = oldIndex;
          const newStart = newIndex;
          do {
            oldIndex += 1;
            newIndex += 1;
          } while (newIndex < end && fingerprint(oldLines[oldIndex].fragment) !== fingerprint(newLines[newIndex].fragment));
          result.append(lineBlock(oldLines.slice(oldStart, oldIndex), "removed"), lineBlock(newLines.slice(newStart, newIndex), "added"));
        }
      }
    }
    return result;
  };
  const compatible = (before, after) => {
    if (before.tagName !== after.tagName || before.namespaceURI !== after.namespaceURI) return false;
    const oldKey = alignmentKey(before, keyAttribute);
    const newKey = alignmentKey(after, keyAttribute);
    return oldKey || newKey ? oldKey === newKey : true;
  };
  const merge = (before, after) => {
    if (fingerprint(before) === fingerprint(after)) return clone(after, "after");
    // Cell insertions or spanning changes need complete old/new rows, not a
    // merged row with extra columns that no longer describe either version.
    const changedColumns = after.tagName === "TR" && (
      before.children.length !== after.children.length
      || Array.from(before.children).some((cell, index) => cell.tagName !== after.children[index].tagName
        || attributes(cell) !== attributes(after.children[index]))
    );
    if (changedColumns || attributes(before) !== attributes(after)) {
      const pair = document.createDocumentFragment();
      pair.append(whole(before, "removed"), whole(after, "added"));
      return pair;
    }
    if (hasBlocks(before) || hasBlocks(after) || structuralTags.has(after.tagName)) {
      const result = clone(after, "after", false);
      result.append(sequence(children(document, before), children(document, after)));
      return result;
    }
    return leaf(before, after);
  };
  const sequence = (before, after, exact = true) => {
    const result = document.createDocumentFragment();
    const changes = arrayChanges(before, after, exact ? (a, b) => fingerprint(a) === fingerprint(b) : compatible);
    let oldIndex = 0;
    let newIndex = 0;
    for (let index = 0; index < changes.length; index += 1) {
      const change = changes[index];
      if (!change.added && !change.removed) {
        for (let count = 0; count < change.count; count += 1) result.append(merge(before[oldIndex++], after[newIndex++]));
      } else if (exact && change.removed && changes[index + 1]?.added) {
        const addition = changes[++index];
        result.append(sequence(before.slice(oldIndex, oldIndex + change.count), after.slice(newIndex, newIndex + addition.count), false));
        oldIndex += change.count;
        newIndex += addition.count;
      } else if (change.removed) {
        for (let count = 0; count < change.count; count += 1) result.append(whole(before[oldIndex++], "removed"));
      } else {
        for (let count = 0; count < change.count; count += 1) result.append(whole(after[newIndex++], "added"));
      }
    }
    return result;
  };

  const before = document.createDocumentFragment();
  const after = document.createDocumentFragment();
  before.append(document.importNode(beforeNode, true));
  after.append(document.importNode(afterNode, true));
  const metadata = metadataSelector ? Array.from(after.querySelectorAll(metadataSelector)) : [];
  if (metadataSelector) {
    for (const node of before.querySelectorAll(metadataSelector)) node.remove();
    for (const node of metadata) node.remove();
  }
  fingerprint(before);
  fingerprint(after);
  const root = document.createElement("section");
  root.className = "w-full min-w-0 text-base-content";
  root.setAttribute("data-content-comparison", "");
  root.setAttribute("aria-label", de ? "Dokumentvergleich" : "Document comparison");
  const legend = document.createElement("p");
  legend.className = "not-prose mx-auto mb-6 max-w-prose border-y border-base-300 py-3 text-sm leading-6 text-base-content";
  legend.setAttribute("data-content-diff-label", "");
  const content = document.createElement("div");
  content.className = "min-w-0 break-words [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto";
  content.setAttribute("data-content-comparison-content", "");
  content.append(sequence(children(document, before), children(document, after)));
  legend.textContent = changed
    ? (de ? "− Entfernt · + Hinzugefügt. Unveränderter Text bleibt im Zusammenhang lesbar."
      : "− Removed · + Added. Unchanged text remains readable in context.")
    : (de ? "Keine Änderungen am Dokumentinhalt." : "No changes to the document content.");
  if (disclaimer) legend.textContent += ` ${disclaimer}`;
  root.append(legend);
  if (metadata.length) {
    const information = document.createElement("div");
    information.className = "mx-auto w-full min-w-0 max-w-prose [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto";
    information.append(...metadata.map((node) => clone(node, "after")));
    root.append(information);
  }
  root.append(content);
  isolateIds(document, root, origins);
  return root;
}
